<!-- filepath: c:\xampp\htdocs\job-platform\src\views\RelatoriosVie.vue -->
<template>
  <div class="relatorios-container container py-5">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start mb-4">
      <div>
        <h1 class="mb-2">Relatórios</h1>
        <p class="text-muted mb-0">
          Painel de métricas para vagas publicadas, inscrições, currículos e evolução de candidaturas.
        </p>
      </div>
      <div class="text-muted small mt-3 mt-md-0">
        Última atualização: {{ dataAtualizacao }}
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <div class="mt-3">Carregando dados do relatório...</div>
    </div>

    <div v-else>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <div class="row g-3 mb-4">
        <div class="col-12 col-sm-6 col-xl-3" v-for="card in overviewCards" :key="card.title">
          <div class="card shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h6 class="card-subtitle text-muted">{{ card.title }}</h6>
                  <h2 class="card-title mb-0">{{ card.value }}</h2>
                </div>
                <i :class="['fs-3', card.icon, card.iconClass]"></i>
              </div>
              <p class="mb-0 text-muted small">{{ card.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row gy-4">
        <div class="col-lg-6">
          <div class="card h-100 shadow-sm">
            <div class="card-header border-0 d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">Inscrições por período</h5>
            </div>
            <div class="card-body">
              <div class="list-group list-group-flush mb-3">
                <div class="list-group-item d-flex justify-content-between align-items-center" v-for="period in periodCards" :key="period.label">
                  <div>
                    <strong>{{ period.label }}</strong>
                    <div class="text-muted small">Últimos {{ period.days }} dias</div>
                  </div>
                  <span class="badge bg-primary rounded-pill">{{ period.value }}</span>
                </div>
              </div>
              <p class="mb-2 text-muted">Evolução diária das inscrições no último mês.</p>
              <div class="table-responsive">
                <table class="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th class="text-end">Inscrições</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in inscricoesPorDia" :key="item.dia">
                      <td>{{ formatarData(item.dia) }}</td>
                      <td class="text-end">{{ item.total }}</td>
                    </tr>
                    <tr v-if="inscricoesPorDia.length === 0">
                      <td colspan="2" class="text-center text-muted">Nenhuma inscrição registrada nos últimos 30 dias.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100 shadow-sm">
            <div class="card-header border-0 d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">Top 5 vagas por inscrições</h5>
            </div>
            <div class="card-body">
              <div v-if="topVagas.length > 0" class="list-group list-group-flush">
                <div class="list-group-item" v-for="vaga in topVagas" :key="vaga.id">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-1">{{ vaga.titulo }}</h6>
                      <small class="text-muted">{{ vaga.total_candidatos }} inscrição(s)</small>
                    </div>
                    <span class="badge bg-success rounded-pill align-self-start">{{ vaga.total_candidatos }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                Nenhuma vaga com inscrições encontradas.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row gy-4 mt-3">
        <div class="col-lg-6">
          <div class="card h-100 shadow-sm">
            <div class="card-header border-0 d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">Status das vagas</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12 col-md-6" v-for="status in vagasPorStatus" :key="status.status">
                  <div class="card border-secondary-subtle h-100">
                    <div class="card-body">
                      <h6 class="card-subtitle text-muted">{{ status.status }}</h6>
                      <p class="display-6 mb-0">{{ status.total }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p class="mt-3 mb-0 text-muted">Resumo de vagas por situação atual.</p>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card h-100 shadow-sm">
            <div class="card-header border-0 d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">Métricas adicionais</h5>
            </div>
            <div class="card-body">
              <ul class="list-group list-group-flush">
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  Usuários cadastrados
                  <span class="badge bg-secondary rounded-pill">{{ summary.total_usuarios }}</span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  Candidatos
                  <span class="badge bg-secondary rounded-pill">{{ summary.total_candidatos }}</span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  Administradores
                  <span class="badge bg-secondary rounded-pill">{{ summary.total_admins }}</span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  Currículos enviados
                  <span class="badge bg-secondary rounded-pill">{{ summary.total_curriculos }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api';

export default {
  name: 'RelatoriosVie',
  data() {
    return {
      loading: true,
      error: '',
      summary: {
        total_vagas: 0,
        vagas_abertas: 0,
        vagas_encerradas: 0,
        total_inscricoes: 0,
        inscricoes_7_dias: 0,
        inscricoes_30_dias: 0,
        inscricoes_90_dias: 0,
        total_usuarios: 0,
        total_candidatos: 0,
        total_admins: 0,
        total_curriculos: 0,
      },
      topVagas: [],
      vagasPorStatus: [],
      inscricoesPorDia: [],
      dataAtualizacao: '',
    };
  },
  computed: {
    overviewCards() {
      return [
        {
          title: 'Vagas publicadas',
          value: this.summary.total_vagas,
          description: 'Total de vagas cadastradas no sistema.',
          icon: 'bi bi-briefcase-fill',
          iconClass: 'text-primary',
        },
        {
          title: 'Inscrições totais',
          value: this.summary.total_inscricoes,
          description: 'Total de candidaturas recebidas.',
          icon: 'bi bi-people-fill',
          iconClass: 'text-success',
        },
        {
          title: 'Candidatos',
          value: this.summary.total_candidatos,
          description: 'Quantidade de usuários do tipo candidato.',
          icon: 'bi bi-person-badge-fill',
          iconClass: 'text-warning',
        },
        {
          title: 'Currículos enviados',
          value: this.summary.total_curriculos,
          description: 'Número de currículos carregados no sistema.',
          icon: 'bi bi-file-earmark-text-fill',
          iconClass: 'text-info',
        },
      ];
    },
    periodCards() {
      return [
        {
          label: 'Últimos 7 dias',
          days: 7,
          value: this.summary.inscricoes_7_dias,
        },
        {
          label: 'Últimos 30 dias',
          days: 30,
          value: this.summary.inscricoes_30_dias,
        },
        {
          label: 'Últimos 90 dias',
          days: 90,
          value: this.summary.inscricoes_90_dias,
        },
      ];
    },
  },
  mounted() {
    this.carregarRelatorios();
  },
  methods: {
    async carregarRelatorios() {
      try {
        const response = await api.get('relatorios.php');
        const data = response.data;

        this.summary = data.summary || this.summary;
        this.topVagas = data.top_vagas || [];
        this.vagasPorStatus = data.vagas_por_status || [];
        this.inscricoesPorDia = data.inscricoes_por_dia || [];
        this.dataAtualizacao = new Date().toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (err) {
        this.error = 'Erro ao carregar os dados do relatório. Tente novamente.';
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    formatarData(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('pt-BR');
    },
  },
};
</script>

<style scoped>
.relatorios-container {
  max-width: 1200px;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

.card-subtitle {
  font-size: 0.9rem;
}

.display-6 {
  font-size: 2rem;
}

.list-group-item {
  border: none;
  padding-left: 0;
  padding-right: 0;
}

.table-responsive {
  max-height: 420px;
  overflow: auto;
}
</style>